import { useEffect, useState } from 'react';
import { devStore, sessionCostUsd, type CallLog } from '../lib/devlog';
import { clearMemo, memoSnapshot, onMemoChange } from '../lib/once';
import { speculationStats } from '../state/speculate';
import { stats as manifestStats, purge as purgeManifest } from '../lib/manifest';
import { runtimeConfig, hasRuntimeConfig } from '../runtime';

/**
 * The instrument. Balance, ledger tail, which cache tier answered each call
 * (straight from find_reusable_asset's trace), and session cost.
 *
 * Every number here is SERVER-REPORTED. Nothing is estimated client-side —
 * a meter that guesses cannot tell you the backend is wrong, which is the
 * only reason this panel exists.
 */
export function DevPanel({ onClose }: { onClose: () => void }) {
  const dev = devStore.use();
  const [, forceMemoRender] = useState(0);
  useEffect(() => onMemoChange(() => forceMemoRender((n) => n + 1)), []);

  const spec = speculationStats();
  const manifest = manifestStats();
  const memo = memoSnapshot();
  const cost = sessionCostUsd();
  const config = hasRuntimeConfig() ? runtimeConfig() : null;

  return (
    <aside className="dev">
      <header>
        <span>showling · dev</span>
        <button className="mini" onClick={onClose}>
          hide
        </button>
      </header>
      <div className="body">
        <h3>credits</h3>
        <div className="kv">
          <span>balance</span>
          <b>{dev.balance === null ? '—' : dev.balance}</b>
        </div>
        <div className="kv">
          <span>session cost (server-reported)</span>
          <b className="big-num">${cost.toFixed(3)}</b>
        </div>
        {cost === 0 && (
          <div className="warn">
            $0.000 means the server reported no tier/costUsd on any asset — not
            that nothing was spent. Candidates carry no tier field yet.
          </div>
        )}

        <h3>ledger tail</h3>
        {dev.ledger.length === 0 && <div>—</div>}
        {dev.ledger.slice(0, 8).map((row, i) => (
          <div className="kv" key={i}>
            <span>{String(row.type ?? '?')}</span>
            <b>
              {String(row.credits ?? '')} · {String(row.at ?? '').slice(0, 19)}
            </b>
          </div>
        ))}

        <h3>speculation</h3>
        <label className="toggle">
          <input
            type="checkbox"
            checked={dev.speculateGate}
            onChange={(e) => devStore.set({ speculateGate: e.target.checked })}
          />
          gate on typing pause (~$0.0004)
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={dev.speculateDrafts}
            onChange={(e) => devStore.set({ speculateDrafts: e.target.checked })}
          />
          draft portraits on gate-allow (~$0.20)
        </label>
        <div className="kv">
          <span>gate calls</span>
          <b>
            {spec.gateCalls} / {spec.maxGate}
          </b>
        </div>
        <div className="kv">
          <span>speculative drafts</span>
          <b>
            {spec.draftSpeculations} / {spec.maxDrafts}
          </b>
        </div>

        <h3>
          in-flight memo ({memo.length}){' '}
          <button className="mini" onClick={clearMemo}>
            clear
          </button>
        </h3>
        <div className="warn">
          Entries are never evicted on success — that is the double-spend guard.
        </div>
        {memo.map((m) => (
          <div className="kv" key={m.key}>
            <span className="path">{m.key}</span>
            <b>{m.settled}</b>
          </div>
        ))}

        <h3>
          device manifest{' '}
          <button className="mini" onClick={purgeManifest}>
            purge
          </button>
        </h3>
        <div className="kv">
          <span>catalogVersion</span>
          <b>{manifest.catalogVersion}</b>
        </div>
        <div className="kv">
          <span>entries</span>
          <b>{manifest.count}</b>
        </div>

        <h3>calls ({dev.calls.length})</h3>
        {dev.calls.map((call) => (
          <CallRow key={call.id} call={call} />
        ))}

        <h3>config</h3>
        <pre>
          {config
            ? JSON.stringify(
                {
                  apiEndpoint: config.apiEndpoint,
                  tenantId: config.tenantId,
                  clientId: config.clientId,
                  apiScope: config.apiScope,
                  mediaDomain: config.mediaDomain,
                },
                null,
                1,
              )
            : '(not loaded)'}
        </pre>
      </div>
    </aside>
  );
}

function CallRow({ call }: { call: CallLog }) {
  const statusClass = `st-${Math.floor(call.status / 100)}`;
  return (
    <div className="call">
      <div className="line1">
        <span className={statusClass}>{call.status || 'ERR'}</span>
        <span className="path">
          {call.method} {call.path}
        </span>
        <span className="ms">{call.ms}ms</span>
      </div>
      {call.note && (
        <div className="ms">
          {call.note}
          {call.speculative && <span className="spec"> · speculative</span>}
        </div>
      )}
      {call.tiers.length > 0 && <div className="trace">tier: {call.tiers.join(', ')}</div>}
      {call.trace.map((step, i) => (
        <div className={`trace ${step.result ?? ''}`} key={i}>
          {step.tier}: {step.result}
          {step.assetId ? ` → ${step.assetId}` : ''}
          {step.detail ? ` (${step.detail})` : ''}
        </div>
      ))}
      {call.error && <div className="trace error">{call.error}</div>}
    </div>
  );
}
